import React from "react"

import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import Headroom from "react-headroom"
import { VideoTag } from "react-video-tag"

import {
  bp,
  bpSm,
  ButtonLink,
  ContentWrapper,
  H1,
  H2,
  H3,
  Layout,
  P,
  shevy,
  theme,
  toEm,
  Wrapper,
} from "../components"
import Header from "../components/blocks/Header"
import { UseInSchool } from "../components/blocks/UseInSchool"
import SEO from "../components/Seo"
import RunIcon from "../images/run.inline.svg"
import RunbitMap from "../images/runbit-map.inline.svg"
import { useTranslations } from "../utils/intl"

const Over100: React.FC = props => {
  const { it } = useTranslations()

  return (
    <ContentWrapper
      css={{
        display: "grid",
        gridTemplateColumns: "1.3fr 1fr",
        alignItems: "center",
        gridGap: shevy.baseSpacing(),
        [bpSm]: {
          gridTemplateColumns: "1fr",
        },
      }}
      {...props}
    >
      <div>
        <H2>
          {it([
            "Over 100 schools are using Runbit",
            "Över 100 st skolor använder Runbit",
          ])}
        </H2>
        <P css={{ maxWidth: toEm(475), marginBottom: 0 }}>
          {it([
            `Today, 100 schools use runbit in Sweden. It takes just under 10 minutes to start using Runbit in teaching.`,
            `Idag är det 100st skolor som använder Runbit i Sverige. Det tar knappt 10 minuter att komma igång och börja använda Runbit i undervisningen.`,
          ])}
        </P>
      </div>
      <div
        css={{
          justifySelf: "center",
          [bpSm]: {
            display: "none",
          },
        }}
      >
        <RunbitMap css={{ width: toEm(254) }} />
      </div>
    </ContentWrapper>
  )
}

const ForAllYears: React.FC = props => {
  const { it } = useTranslations()

  return (
    <ContentWrapper css={{ textAlign: "center" }} {...props}>
      <H2>{it(["For all grades", "För alla årskurser"])}</H2>
      <P>
        {it([
          "Runbit is very easy to use and is available for Android, iPhone and iPad.",
          "Runbit är väldigt enkel att använda och finns för Android, iPhone och iPad.",
        ])}
      </P>
      <VideoTag
        css={{ marginTop: toEm(97), width: "100%" }}
        autoPlay={true}
        muted={true}
        playsInline={true}
        loop={true}
        src="https://d1yj9whwrn10mt.cloudfront.net/runbit.mp4"
      />
    </ContentWrapper>
  )
}

const GroupInfo: React.FC = props => {
  const { it } = useTranslations()
  return (
    <ContentWrapper
      css={{
        display: "grid",
        gridTemplateColumns: "1fr 1.2fr",
        gridGap: toEm(48),
        [bpSm]: {
          gridTemplateColumns: "1fr",
        },
      }}
      {...props}
    >
      <div
        css={{
          justifySelf: "center",
          alignSelf: "center",
          [bpSm]: {
            display: "none",
          },
        }}
      >
        <img
          css={{ width: toEm(400) }}
          src={require("../images/travelers.svg").default}
        />
      </div>
      <div>
        <H2
          css={{
            marginBottom: shevy.baseSpacing(),
            [bp]: {
              marginBottom: toEm(60, 60),
            },
          }}
        >
          {it(["Advantages with groups", "Fördelar med grupper"])}
        </H2>
        {[
          {
            title: it(["Challenge each other", "Utmana varandra"]),
            text: it([
              `Group members can challenge each other in various activities.`,
              `Gruppmedlemmar kan utmana varandra i olika aktiviteter.`,
            ]),
            image: require("../images/standings.svg").default,
          },
          {
            title: it(["Unlimited members", "Obegränsat antal"]),
            text: it([
              `Each group has no limit on members, and you can join as many groups as you want.`,
              `Varje grupp kan ha obegränsat antal medlemmar och man kan skapa så många grupper man vill.`,
            ]),
            image: require("../images/hearts.svg").default,
          },
        ].map(({ title, text, image }) => (
          <div
            key={title}
            css={{
              display: "flex",
              ":not(last-child)": { marginBottom: toEm(48) },
            }}
          >
            <div css={{ paddingRight: toEm(32) }}>
              <img
                css={{
                  width: toEm(122),
                  [bpSm]: {
                    width: "12.8vw",
                  },
                }}
                src={image}
              />
            </div>
            <div
              css={{
                paddingTop: toEm(16),
                [bpSm]: {
                  paddingTop: "0.8vw",
                },
              }}
            >
              <H3
                css={{
                  fontSize: toEm(30),
                  marginBottom: toEm(4, 30),
                }}
              >
                {title}
              </H3>
              <p css={{ ...shevy.body }}>{text}</p>
            </div>
          </div>
        ))}
      </div>
    </ContentWrapper>
  )
}

const GetStarted: React.FC = props => {
  const { it } = useTranslations()

  const info = [
    {
      title: it(["A Runbit account", "Ett Runbit-konto"]),
      text: it([
        `You as a teacher need an account to manage groups and to be able to invite students. Several accounts can administer the same group.`,
        `Du som idrottslärare behöver ett konto för att hantera grupper och för att kunna bjuda in elever. Ni kan vara flera konton som administrerar en grupp.`,
      ]),
      image: require("../images/run.svg").default,
    },
    {
      title: it(["Create one or more groups", "Skapa en eller flera grupper"]),
      text: it([
        `Sign in with your account and create a group in the app. Either for the whole school or maybe for each grade. You can also invite other teachers to the group.`,
        `Logga in med ditt konto och skapa en grupp i appen. Antingen för hela skolan eller kanske en grupp per klassrum. Bjud även in andra idrottslärare till gruppen.`,
      ]),
      image: require("../images/a-to-b.svg").default,
    },
  ]
  return (
    <ContentWrapper {...props}>
      <H2
        css={{ maxWidth: toEm(743, 60), "&&": { marginBottom: toEm(60, 60) } }}
      >
        {it([
          "What is required to get started?",
          "Vad behövs för att komma igång?",
        ])}
      </H2>
      {info.map(({ title, text, image }) => (
        <div
          key={title}
          css={{
            display: "flex",
            maxWidth: toEm(920),
            ":not(last-child)": { marginBottom: toEm(48) },
          }}
        >
          <div css={{ paddingRight: toEm(32) }}>
            <img
              src={image}
              css={{
                [bpSm]: {
                  width: "12.8vw",
                },
              }}
            />
          </div>
          <div
            css={{
              paddingTop: toEm(16),
              [bpSm]: {
                paddingTop: "0.8vw",
              },
            }}
          >
            <H3
              css={{
                fontSize: toEm(30),
                marginBottom: toEm(4, 30),
              }}
            >
              {title}
            </H3>
            <p css={{ ...shevy.body }}>{text}</p>
          </div>
        </div>
      ))}
    </ContentWrapper>
  )
}

const GirlPhoneHand: React.FC = props => {
  const {
    file: { childImageSharp: image },
  } = useStaticQuery(graphql`
    query {
      file(relativePath: { eq: "girl-phone-hand.png" }) {
        childImageSharp {
          gatsbyImageData(
            sizes: "100vh"
            quality: 95
            breakpoints: [500, 960, 1024, 1920, 2080]
            placeholder: TRACED_SVG
          )
        }
      }
    }
  `)

  return (
    <GatsbyImage
      css={{
        width: "100%",
        objectFit: "cover",
        height: toEm(692),
      }}
      {...props}
      alt="Girl holding a phone showing the runbit app"
      image={getImage(image)!}
    />
  )
}

const MiniFAQ: React.FC = props => {
  const { it } = useTranslations()

  const info = [
    {
      title: it(["Does the app require internet?", "Kräver appen surf?"]),
      text: it([
        `Runbit needs internet access to show the map, load missions, to save, and to check that the user has the right to use the app free of charge.

        It is possible to use Runbit without internet if you have created an assignment on Wifi before, but we still recommend having access to the internet when the app is used. The app normally uses less than 1 megabyte per hour if the map has been loaded before.`,
        `Runbit behöver tillgång till internet för att kartan, uppdragen, spara och för att kontrollera att användaren har rätt att använda appen utan kostnad.

      Det går använda Runbit utan internet om man har skapar ett uppdrag på Wifi innan men vi rekommenderar ändå att ha tillgång till internet när appen används. Appen använder normalt under 1 megabyte per timme om kartan laddats in tidigare.`,
      ]),
      color: theme.colors.blue,
    },
    {
      title: it(["Is iPad supported?", "Har ni stöd för iPad?"]),
      text: it([
        <>
          Runbit can be run on an iPad without any problems. We recommend using
          an iPad that supports SIM cards as devices that only have WiFi do not
          have GPS.{"\n\n"}If the app is only to be used on the school's iPad,
          we recommend buying <a href="https://kartjakt.se">Kartjakt</a>{" "}
          instead. Kartjakt is a simpler version of Runbit that does not require
          accounts and has better support for iPads and can be used completely
          without internet if it is started on Wifi once.
        </>,
        <>
          Runbit går att köra på en iPad utan problem. Vi rekommenderar att
          använda en iPad som har stöd för sim-kort då enheter som endast har
          WiFi saknar GPS.{"\n\n"}Ska appen endast användas på skolans iPad kan
          jag rekommendera att köpa <a href="https://kartjakt.se">Kartjakt</a>{" "}
          istället. Kartjakt är en enklare version av Runbit som inte kräver
          konton och har ett bättre stöd för iPads och går använda helt utan
          internet om den startas på Wifi en gång.
        </>,
      ]),
      color: theme.colors.green,
    },
    {
      title: it(["Some students don't have a phone", "Alla har inte en mobil"]),
      text: it([
        `We are aware that not all students have access to their own smartphone and in that case we recommend using Runbit in groups that share smartphones.

        The app works just as well in sports class as at home. It is not uncommon for parents to install the app to take the whole family out for a walk as homework to develop some positive habits.`,
        `Vi är medveten om att alla elever inte har tillgång till egen mobil och rekommenderar därför att använda Runbit i grupper och delar på en mobil.

        Appen funkar lika bra på idrotten som hemma. Det inte ovanligt att föräldrarna installerar appen för att som hemläxa ta med hela familjen ut på en promenad för att skapa lite positiva vanor.`,
      ]),
      color: theme.colors.red,
    },
  ]

  return (
    <ContentWrapper {...props}>
      {info.map(({ title, text, color }) => (
        <div
          key={title}
          css={{
            display: "flex",
            ":not(last-child)": { marginBottom: toEm(48) },
          }}
        >
          <div
            css={{
              paddingRight: toEm(32),
              "@media(max-width: 500px)": { display: "none" },
            }}
          >
            <RunIcon
              css={{
                circle: { fill: color },
                [bpSm]: {
                  width: "12.8vw",
                  height: "auto",
                },
              }}
            />
          </div>
          <div
            css={{
              paddingTop: toEm(12),
              [bpSm]: {
                paddingTop: "0.8vw",
              },
            }}
          >
            <H3
              css={
                {
                  // fontSize: toEm(30),
                  // marginBottom: toEm(4, 30),
                }
              }
            >
              {title}
            </H3>
            <P css={{ ...shevy.body, whiteSpace: "pre-line" }}>{text}</P>
          </div>
        </div>
      ))}
    </ContentWrapper>
  )
}

const School: React.FC = () => {
  const { it } = useTranslations()

  return (
    <Wrapper>
      <SEO title={it(["Use Runbit at school", "Använd Runbit i skolan"])} />
      <Layout
        css={{
          paddingBottom: toEm(64 * 2),
          "& > div, & > img, & > section": {
            marginTop: toEm(64 * 2),
          },
        }}
      >
        <Headroom css={{ "&&": { marginTop: 0 } }}>
          <Header css={{ top: 0, left: 0, right: 0 }} />
        </Headroom>
        <UseInSchool>
          <H1 css={{ ...shevy.h2 }}>
            {it(["Use Runbit at school!", "Använd Runbit i skolan!"])}
          </H1>
          <P>
            {it([
              "Teach students how maps work, use for orientation or just motivate students to develop healthier habits.",
              "Lär eleverna hur kartor fungerar, använd för orientering eller bara för att motivera eleverna till hälsosammare vanor.",
            ])}
          </P>
        </UseInSchool>
        <Over100 />
        <ForAllYears />
        <GroupInfo />
        <GetStarted />
        <GirlPhoneHand />
        <MiniFAQ />
      </Layout>
    </Wrapper>
  )
}

export default School
